import { track } from "@/lib/track";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, Phone, Clock, Plane } from "lucide-react";
import { Helmet } from "react-helmet-async";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import SubmissionForm from "@/components/SubmissionForm";
import { useUI } from "@/context/UIContext";
import { btnPrimary, btnOutline, overline } from "@/components/kit";
import { CONTACT } from "@/data/site";
import { DESTINATIONS, DESTINATION_ORDER } from "@/data/destinations";

export default function VoyageDetail() {
  const { slug } = useParams();
  const { setCallbackOpen } = useUI();
  const d = DESTINATIONS[slug];
  if (!d) return <Navigate to="/voyages" replace />;

  const idx = DESTINATION_ORDER.indexOf(slug);
  const next = DESTINATIONS[DESTINATION_ORDER[(idx + 1) % DESTINATION_ORDER.length]];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TravelAgency",
    name: `Désir Events — Voyages incentives ${d.name}`,
    url: `https://desir-events.com/voyages/${d.slug}`,
    description: d.seoDesc,
    areaServed: { "@type": "Country", name: d.name },
    provider: { "@type": "Organization", name: "Désir Events", url: "https://desir-events.com" },
  };

  return (
    <>
      <Seo title={`Voyage entreprise ${d.name}`} description={d.seoDesc} keywords={d.seoKeywords} path={`/voyages/${d.slug}`} />
      <Helmet><script type="application/ld+json">{JSON.stringify(jsonLd)}</script></Helmet>

      {/* Hero */}
      <section className="relative h-[80vh] min-h-[560px] overflow-hidden pt-20" data-testid="voyage-hero">
        <img src={d.hero} alt={`Voyage entreprise ${d.name}`} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1440px] flex-col justify-end px-5 pb-16 md:px-10 md:pb-24">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">Voyage incentive</span>
            <span className="text-3xl">{d.flag}</span>
          </motion.div>
          <motion.h1 initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="mt-4 font-serif text-6xl font-medium leading-none tracking-tight text-white md:text-9xl">
            {d.name}
          </motion.h1>
          <p className="mt-6 max-w-2xl font-serif text-2xl italic text-lime md:text-3xl">{d.tagline}</p>
          <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-white/80">
            <span className="flex items-center gap-2"><Plane size={16} className="text-lime" /> {d.from}</span>
            <span className="flex items-center gap-2"><Clock size={16} className="text-lime" /> {d.duration}</span>
          </div>
        </div>
      </section>

      {/* Intro + CTA */}
      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-10 md:py-28">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-7">
            <Reveal><span className={overline}>Pourquoi {d.name} ?</span></Reveal>
            <Reveal delay={1}>
              <p className="mt-6 font-serif text-3xl leading-snug text-ink md:text-4xl">{d.intro}</p>
            </Reveal>
            {d.body.map((p, i) => (
              <Reveal key={i} delay={2 + i}>
                <p className="mt-8 text-lg leading-relaxed text-ink/65">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <div className="md:sticky md:top-32 rounded-2xl border border-ink/10 bg-mist p-8">
              <span className={overline}>Votre projet</span>
              <h3 className="mt-4 font-serif text-3xl text-ink">Un projet<br />au {d.name} ?</h3>
              <p className="mt-4 text-sm leading-relaxed text-ink/60">Nos experts vous répondent sous 24h avec une première estimation budgétaire.</p>
              <Link to="/devis" className={`${btnPrimary} mt-6 w-full justify-center`} data-testid="voyage-devis-btn">
                Déposer mon brief <ArrowRight size={16} />
              </Link>
              <button onClick={() => { track("click_rappel"); setCallbackOpen(true); }} className={`${btnOutline} mt-3 w-full justify-center`}>Être rappelé</button>
              <a href={`tel:${CONTACT.phoneMobile.replace(/\s/g, "")}`} className="mt-6 flex items-center gap-3 border-t border-ink/10 pt-6 text-sm text-ink/70 hover:text-lime-dim" data-testid="voyage-phone">
                <Phone size={16} className="text-lime-dim" />
                <span>Ou appelez <strong className="text-ink">{CONTACT.phoneMobile}</strong></span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Villes & Activités */}
      <section className="border-t border-ink/10 bg-mist py-20 md:py-28" data-testid="voyage-content">
        <div className="mx-auto grid max-w-[1440px] gap-14 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-5">
            <Reveal><span className={overline}>Nos destinations {d.name}</span></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                Villes couvertes<br /><span className="italic text-lime-dim">au {d.name}</span>
              </h2>
            </Reveal>
            <div className="mt-8 flex flex-wrap gap-2">
              {d.cities.map((c) => (
                <span key={c} className="rounded-full border border-ink/15 bg-white px-4 py-2 text-sm text-ink/70">{c}</span>
              ))}
            </div>

            {/* Gallery */}
            <div className="mt-10 grid grid-cols-3 gap-3">
              {d.gallery.map((g, i) => (
                <motion.img
                  key={i}
                  src={g}
                  alt={`${d.name} ${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`w-full rounded-xl object-cover ${i === 0 ? "col-span-2 h-52" : "h-52"}`}
                />
              ))}
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <Reveal><span className={overline}>Programmes signature</span></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                Activités & Expériences
              </h2>
            </Reveal>
            <ul className="mt-8 space-y-px overflow-hidden rounded-2xl border border-ink/10">
              {d.activities.map((a, i) => (
                <Reveal key={i} delay={i % 3}>
                  <li className="flex items-start gap-4 bg-white p-5" data-testid={`activity-${i}`}>
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-ink"><Check size={13} /></span>
                    <span className="text-base text-ink/80">{a}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact form */}
      <section className="border-t border-ink/10 bg-white" data-testid="voyage-form-section">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-5">
            <Reveal><span className={overline}>Discutons de votre voyage {d.name}</span></Reveal>
            <Reveal delay={1}>
              <h2 className="mt-6 font-serif text-4xl font-medium leading-tight tracking-tight text-ink md:text-5xl">
                On construit votre voyage <span className="italic text-lime-dim">{d.name}</span> ensemble.
              </h2>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-ink/60">
                Nombre de participants, dates, budget, objectif : décrivez-nous votre projet, nous revenons vers vous sous 24h avec une première proposition.
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-2xl border border-ink/10 bg-mist p-8 md:p-10">
              <SubmissionForm
                type="devis"
                testid={`voyage-form-${d.slug}`}
                submitLabel={`Envoyer ma demande ${d.name}`}
                fields={[
                  { name: "name", label: "Votre nom", required: true },
                  { name: "company", label: "Entreprise", required: true },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Téléphone", type: "tel" },
                  { name: "message", label: `Votre projet de voyage au ${d.name}`, type: "textarea", full: true, required: true, placeholder: "Dates envisagées, nombre de participants, budget indicatif, objectif (cohésion, récompense, séminaire stratégique)…" },
                ]}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Next destination */}
      <section className="border-t border-ink/10 bg-ink text-white">
        <Link to={`/voyages/${next.slug}`} className="group mx-auto flex max-w-[1440px] items-center justify-between px-5 py-16 md:px-10 md:py-24" data-testid="next-voyage">
          <div>
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-lime">Destination suivante</span>
            <h3 className="mt-3 flex items-center gap-4 font-serif text-4xl font-medium text-white group-hover:text-lime md:text-6xl">
              <span className="text-3xl md:text-5xl">{next.flag}</span>
              {next.name}
            </h3>
          </div>
          <ArrowUpRight size={44} className="text-white/40 transition-transform duration-300 group-hover:translate-x-2 group-hover:-translate-y-2 group-hover:text-lime" />
        </Link>
      </section>
    </>
  );
}
